import { useEffect, useRef, useState } from "react";
import { Upload, Wallet, Clock3, Landmark, Copy } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";

function formatRp(n) { return `Rp ${Number(n || 0).toLocaleString("id-ID")}`; }

export default function JamaahPembayaran() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(null);
  const fileInputs = useRef({});

  const load = () => axios.get(`${API}/jamaah/pembayaran`).then(({ data }) => setData(data)).catch(() => setData({ profile_status: null, payments: [], bank_accounts: [] }));
  useEffect(() => { load(); }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Nomor rekening disalin");
  };

  const handleUpload = async (paymentId, file) => {
    if (!file) return;
    setUploading(paymentId);
    const formData = new FormData();
    formData.append("payment_id", paymentId);
    formData.append("file", file);
    try {
      await axios.post(`${API}/uploads/payment-proof`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bukti pembayaran berhasil diunggah");
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setUploading(null);
    }
  };

  if (data === null) return <LoadingScreen />;

  const { profile_status, payments, bank_accounts } = data;

  return (
    <div data-testid="jamaah-pembayaran-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Pembayaran</h1>
        <p className="text-sm text-[#64748B]">Pantau progres dan riwayat pembayaran program Anda.</p>
      </div>

      {payments.length === 0 ? (
        profile_status === "prospective" ? (
          <EmptyState
            testId="jamaah-payment-waiting-approval"
            icon={Clock3}
            title="Menunggu Persetujuan Pendaftaran"
            description="Tagihan akan dikirim oleh admin setelah pendaftaran Anda disetujui."
          />
        ) : (
          <EmptyState
            testId="jamaah-payment-waiting-invoice"
            icon={Wallet}
            title="Menunggu Tagihan dari Admin"
            description="Pendaftaran Anda sudah disetujui. Admin akan mengirimkan rincian tagihan pembayaran ke dashboard ini."
          />
        )
      ) : (
        <div className="space-y-4">
          {bank_accounts.length > 0 && (
            <div className="rounded-2xl border border-[#F5B700]/30 bg-[#FFFBEB] p-6" data-testid="jamaah-bank-accounts-panel">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-[#92720C]"><Landmark className="h-4 w-4" /> Rekening Transfer Manual</h3>
              <p className="mt-1 text-xs text-[#92720C]/80">Lakukan pembayaran ke salah satu rekening berikut, lalu unggah bukti transfer pada tagihan Anda.</p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {bank_accounts.map((b) => (
                  <div key={b.id} data-testid={`jamaah-bank-account-${b.id}`} className="rounded-xl bg-white p-4 shadow-sm">
                    <p className="font-heading text-sm font-bold text-[#1F2937]">{b.bank_name}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="font-mono text-lg font-bold text-[#0F6B4F]">{b.account_number}</p>
                      <button data-testid={`jamaah-bank-copy-${b.id}`} onClick={() => copy(b.account_number)} className="rounded-full bg-[#0F6B4F]/10 p-1.5 text-[#0F6B4F] hover:bg-[#0F6B4F]/20"><Copy className="h-3 w-3" /></button>
                    </div>
                    <p className="text-xs text-[#64748B]">a.n. {b.account_holder}{b.branch ? ` — ${b.branch}` : ""}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {payments.map((p) => {
            const pct = p.total_fee ? Math.min(Math.round((p.amount_paid / p.total_fee) * 100), 100) : 0;
            return (
              <div key={p.id} data-testid={`jamaah-payment-card-${p.id}`} className="rounded-2xl bg-white p-6 shadow-[0_2px_10px_rgba(15,107,79,0.05)]">
                <div className="flex items-center justify-between">
                  <p className="font-heading text-lg font-bold text-[#0F6B4F]">{formatRp(p.total_fee)}</p>
                  <StatusBadge status={p.status} />
                </div>
                <p className="text-sm text-[#64748B]">Terbayar: {formatRp(p.amount_paid)} &middot; Sisa: {formatRp(p.total_fee - p.amount_paid)}</p>
                <Progress value={pct} className="mt-3 h-2.5" />
                <p className="mt-1 text-xs text-[#64748B]">{pct}% Lunas</p>
                {p.due_date && <p className="mt-2 text-xs text-[#64748B]">Jatuh tempo: {new Date(p.due_date).toLocaleDateString("id-ID")}</p>}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" ref={(el) => (fileInputs.current[p.id] = el)} onChange={(e) => handleUpload(p.id, e.target.files[0])} className="hidden" data-testid={`jamaah-payment-file-input-${p.id}`} />
                {p.status !== "fully_paid" && (
                  <Button variant="outline" size="sm" disabled={uploading === p.id} data-testid={`jamaah-payment-upload-button-${p.id}`} onClick={() => fileInputs.current[p.id]?.click()} className="mt-4 gap-2 rounded-full">
                    <Upload className="h-3.5 w-3.5" /> {uploading === p.id ? "Mengunggah..." : "Unggah Bukti Pembayaran"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
